import type { SelectTypes } from "../../util/types";
import type { QuestionModule } from "../contracts";
import { selectViewFromStyle } from "../display";
import {
  selectCore,
  type SelectDefinition,
  type SelectDraft,
  type SelectEvent,
  type SelectResponse,
} from "./core";
import { SelectButtonsView, SelectDropdownView, SelectListView } from "./views";

export const selectModule: QuestionModule<
  SelectTypes,
  SelectDefinition,
  SelectDraft,
  SelectEvent,
  SelectResponse
> = {
  type: "select",
  galleryFeatures: [
    "visual-groups",
    "baseline",
    "images",
    "disabled",
    "custom-text",
    "non-contiguous-ids",
    "restored",
  ],
  core: selectCore,
  prompt: (definition) => ({
    content: definition.prompt,
    image: definition.image,
    altText: definition.altText,
    error: definition.error,
  }),
  views: {
    buttons: {
      id: "buttons",
      label: "Buttons",
      component: SelectButtonsView,
      arrangements: ["compact", "wide"],
      requiredControls: [],
      primaryActionOwner: "view",
    },
    list: {
      id: "list",
      label: "List",
      component: SelectListView,
      arrangements: ["compact", "wide"],
      requiredControls: [],
      primaryActionOwner: "view",
    },
    dropdown: {
      id: "dropdown",
      label: "Dropdown",
      component: SelectDropdownView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
      primaryActionOwner: "view",
      space: "expanded",
    },
  },
  defaultDisplay: { view: "buttons" },
  resolveDisplay: (raw) => ({ view: selectViewFromStyle(raw.style) }),
  resolveSpace: (definition) =>
    definition.options.length === 1 ? "expanded" : undefined,
  controls: (definition, draft, display, copy) => {
    const selected = definition.options.find(
      (option) => option.id === draft.selectedOptionId,
    );
    const customPrompt = selected?.customPrompt;
    const needsCustomText = customPrompt !== undefined;
    const needsSubmit = display.view === "dropdown" || needsCustomText;

    return {
      showChoiceHint:
        display.view !== "dropdown" && definition.options.length > 1,
      specify: definition.options.flatMap((option) =>
        option.customPrompt === undefined
          ? []
          : [
              {
                target: { kind: "option-answer" as const, optionId: option.id },
                prompt: option.customPrompt,
                value:
                  draft.selectedOptionId === option.id ? draft.customText : "",
                selected: draft.selectedOptionId === option.id,
                disabled: option.disabled,
                confirm: (value: string): SelectEvent => ({
                  type: "set-custom-answer",
                  optionId: option.id,
                  value,
                }),
                remove: { type: "clear" } as SelectEvent,
                submitOnConfirm: true,
              },
            ],
      ),
      ...(needsSubmit
        ? {
            primaryAction: {
              purpose: "submit" as const,
              label: copy.actions.send,
              action: { type: "submit" as const },
            },
          }
        : {}),
    };
  },
};
